import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import clinicService from '../../../../../services/clinic.service';
import { formatPmsType, PMS_TYPES } from '../../../../../constants/pmsTypes';
import Loading from '../../../../common/Loading';

function PmsCard({ onPmsTypeChange }) {
    const { clinicId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pms, setPms] = useState(null);

    const { register, handleSubmit, reset } = useForm({
        defaultValues: { pmsType: 'NONE' },
    });

    const notifyPmsTypeChange = (nextPmsType) => {
        if (typeof onPmsTypeChange === 'function') {
            onPmsTypeChange(nextPmsType || 'NONE');
        }
    };

    const loadPms = () => {
        setIsLoading(true);
        clinicService
            .getPms(clinicId)
            .then((res) => {
                const data = res.data || { clinicId, pmsType: 'NONE' };
                setPms(data);
                reset({ pmsType: data.pmsType || 'NONE' });
                notifyPmsTypeChange(data.pmsType);
            })
            .catch(() => {
                toast.error('Error while getting PMS', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadPms();
    }, [clinicId]);

    const onSubmit = (formData) => {
        setIsSaving(true);
        clinicService
            .savePms(clinicId, { pmsType: formData.pmsType || 'NONE' })
            .then((res) => {
                toast.success('Updated PMS', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                setIsEditing(false);
                const updated = res.data || { clinicId, pmsType: formData.pmsType || 'NONE' };
                setPms(updated);
                reset({ pmsType: updated.pmsType || 'NONE' });
                notifyPmsTypeChange(updated.pmsType);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error while updating PMS', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => setIsSaving(false));
    };

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-hdd-network"></i>
                    PMS
                </span>
                {pms && (
                    <button
                        type="button"
                        className={`btn btn-sm ${isEditing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                        onClick={() => {
                            if (isEditing) {
                                reset({ pmsType: pms.pmsType || 'NONE' });
                            }
                            setIsEditing(!isEditing);
                        }}
                        disabled={isSaving}
                    >
                        <i className={`bi ${isEditing ? 'bi-x' : 'bi-pencil-square'}`}></i>
                        {isEditing ? ' Cancel' : ' Edit'}
                    </button>
                )}
            </div>
            <div className="notifications-panel__body">
                {isLoading ? (
                    <Loading />
                ) : isEditing ? (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <label htmlFor="pmsType" className="notifications-form-label">
                            Practice Management System
                        </label>
                        <select
                            className="form-select form-select-sm"
                            id="pmsType"
                            disabled={isSaving}
                            {...register('pmsType', { required: true })}
                        >
                            {PMS_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Configure Optosys import under the Optosys tab when set to Optosys.
                        </div>
                        <div className="d-flex mt-3 pt-2 border-top">
                            <button className="btn btn-success btn-sm" type="submit" disabled={isSaving}>
                                {isSaving ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-floppy me-1"></i>
                                        Save
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                ) : pms ? (
                    <div className="notifications-info-value">{formatPmsType(pms.pmsType)}</div>
                ) : (
                    <div className="notifications-empty-state py-3">
                        <i className="bi bi-hdd-network"></i>
                        No PMS configured.
                    </div>
                )}
            </div>
        </div>
    );
}

export default PmsCard;
